// Safe user-facing copy for status-transition failures.
//
// Maps the small set of structured rejection reasons the backend
// can emit (dependency gate, missing cancellation reason) to short
// human strings. Anything we don't recognise falls through to a
// generic "couldn't update" line; we never show the raw backend
// detail because it's developer prose.
//
// v3.19 — the reviewer-approval gate on ``done`` was removed.
// Reviewer approval is now an optional, parallel action via
// /review; ``done`` is no longer gated on it. The reviewer-gate
// branch was dropped here.

export function describeTransitionError(error: unknown): string | null {
  if (!error) return null;
  const bodyDetail =
    error instanceof Object && "body" in error
      ? String(
          (error as { body?: { detail?: string } }).body?.detail ?? "",
        )
      : "";
  const message = error instanceof Error ? error.message : "";
  const haystack = `${bodyDetail} ${message}`.toLowerCase();
  if (/dependency/.test(haystack)) {
    return "This Commitment cannot move forward until its dependency is complete.";
  }
  if (/cancellation_reason/.test(haystack)) {
    return "Cancellation reason is required.";
  }
  // Visibility transition rejections — BE messaging is already user-safe;
  // pass it through verbatim so the inline error matches what the user
  // tried to do (otherwise the generic fallback would mask the cause).
  if (/only the creator/.test(haystack)) {
    return "Only the creator can make this Commitment private.";
  }
  if (/playbook-generated/.test(haystack)) {
    return "Playbook-generated Commitments must remain team-visible.";
  }
  if (/private commitments can only be assigned/.test(haystack)) {
    return "Private Commitments can only be assigned to you.";
  }
  if (/only active commitments can be made private/.test(haystack)) {
    return "Only active Commitments can be made private.";
  }
  // Reviewer/assignee invariants — surface the BE prose verbatim, it
  // is already user-safe and matches what the user attempted.
  if (/private commitments cannot have reviewers/.test(haystack)) {
    return "Private Commitments cannot have reviewers.";
  }
  if (/reviewer must be different from assignee/.test(haystack)) {
    return "Reviewer must be different from assignee.";
  }
  return "Couldn't update the Commitment status. Refresh and try again.";
}
